import {
  Box,
  Checkbox,
  Chip,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import type { AutoTransactionRuleDto } from "../../../../api/types";
import { AdminTileCard } from "../../../../components/AdminList";
import { CopyableText } from "../../../../components/CopyableText";
import {
  summarizeActions,
  summarizeCondition,
  type NameResolvers,
} from "../../../../logic/auto-transaction-summary";
import { useRegistry } from "../../../../context/RegistryContext";
import { AutoTransactionActions } from "./AutoTransactionActions";

export type AutoTransactionTileProps = {
  rule: AutoTransactionRuleDto;
  isTogglingEnabled: boolean;
  selection?: {
    selected: boolean;
    onToggle: () => void;
  } | null;
  onEdit: (rule: AutoTransactionRuleDto) => void;
  onDuplicate: (rule: AutoTransactionRuleDto) => void;
  onPreviewMatches: (rule: AutoTransactionRuleDto) => void;
  onShowApplied: (rule: AutoTransactionRuleDto) => void;
  onDelete: (rule: AutoTransactionRuleDto) => void;
  onToggleEnabled: (rule: AutoTransactionRuleDto, next: boolean) => void;
};

export const AutoTransactionTile = ({
  rule,
  isTogglingEnabled,
  selection,
  onEdit,
  onDuplicate,
  onPreviewMatches,
  onShowApplied,
  onDelete,
  onToggleEnabled,
}: AutoTransactionTileProps) => {
  // Hidden included, as `useRuleSetNames` does for the other summaries: a rule
  // that cites a hidden entity must still be able to name it.
  const reg = useRegistry().withHidden;
  const resolvers: NameResolvers = {
    source: (id) => reg.sources.find((s) => s.id === id)?.name,
    group: (id) => reg.groups.find((g) => g.id === id)?.name,
    category: (id) => reg.categories.find((c) => c.id === id)?.name,
    expenseType: (id) => reg.expenseTypes.find((e) => e.id === id)?.name,
  };
  const condition = summarizeCondition(rule.condition);
  const actions = summarizeActions(rule.actions, resolvers);
  return (
    <AdminTileCard
      footer={
        <AutoTransactionActions
          size="small"
          rule={rule}
          isTogglingEnabled={isTogglingEnabled}
          onEdit={onEdit}
          onDuplicate={onDuplicate}
          onPreviewMatches={onPreviewMatches}
          onShowApplied={onShowApplied}
          onDelete={onDelete}
          onToggleEnabled={onToggleEnabled}
        />
      }
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        {selection && (
          <Checkbox
            size="small"
            sx={{ p: 0 }}
            checked={selection.selected}
            onChange={selection.onToggle}
            slotProps={{ input: { "aria-label": `select ${rule.name}` } }}
          />
        )}
        <Chip
          size="small"
          label={`P${rule.priority}`}
          color={rule.enabled ? "primary" : "default"}
          variant={rule.enabled ? "filled" : "outlined"}
          sx={{ fontVariantNumeric: "tabular-nums" }}
        />
        {rule.editable ? (
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => onEdit(rule)}
            aria-label={`Edit rule ${rule.name}`}
            title={rule.name}
            underline="hover"
            sx={{
              fontWeight: 600,
              textAlign: "left",
              color: rule.enabled ? "text.primary" : "text.disabled",
              textDecoration: rule.enabled ? "none" : "line-through",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flexGrow: 1,
              minWidth: 0,
            }}
          >
            {rule.name}
          </Link>
        ) : (
          // Read-only virtual rule: the name is not an edit link.
          <Typography
            variant="body2"
            title={rule.name}
            sx={{
              fontWeight: 600,
              textAlign: "left",
              color: "text.primary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flexGrow: 1,
              minWidth: 0,
            }}
          >
            {rule.name}
          </Typography>
        )}
      </Stack>
      <Tooltip
        title={
          <Box component="span" sx={{ whiteSpace: "pre-wrap" }}>
            {condition.full}
          </Box>
        }
        placement="top-start"
      >
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          when {condition.short}
        </Typography>
      </Tooltip>
      <Tooltip
        title={
          <Box component="span" sx={{ whiteSpace: "pre-wrap" }}>
            {actions.full}
          </Box>
        }
        placement="top-start"
      >
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          then {actions.short}
        </Typography>
      </Tooltip>
      {rule.description && (
        <Typography
          variant="caption"
          sx={{ fontStyle: "italic", color: "text.secondary" }}
        >
          {rule.description}
        </Typography>
      )}
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        <CopyableText text={rule.id} maxLength={10} />
      </Typography>
    </AdminTileCard>
  );
};
